import React, { useState, Suspense } from 'react';

const EDMRichEditor = React.lazy(() => import('./EDM/EDMRichEditor'));

interface NewsletterData {
  subject_line?: string;
  preview_text?: string;
  body_html?: string;
  body_plain?: string;
  alternative_subjects?: string[];
  recommended_send_time?: string;
  audience_segment?: string;
}

interface EditableNewsletterViewerProps {
  rawContent: string;
  onSave: (updatedRaw: string) => void;
  onCancel: () => void;
  projectId?: string;
}

// ── Parser ────────────────────────────────────────────────────────────────────
// Mirrors backend _parse_synthesized_result for newsletter tasks.
// Handles:
//   1. Clean JSON
//   2. {"response": "```json\n{...}\n```", ...}  (double-quoted outer)
//   3. Python-repr  {'response': '```json\n{...}\n```', ...}  (single-quoted outer)
const parseNewsletterContent = (raw: string): NewsletterData | null => {
  const trimmed = (raw || '').trim();
  const NL_KEYS = ['subject_line', 'body_html', 'body_plain', 'subject_line'];
  const hasNLKeys = (obj: any) =>
    obj && typeof obj === 'object' && NL_KEYS.some(k => k in obj);

  // Helper: strip ```json…``` fences and parse
  const parseFenced = (s: string): NewsletterData | null => {
    const m = s.match(/```(?:json)?\s*([\s\S]*?)```/);
    const src = m ? m[1] : s;
    try {
      const p = JSON.parse(src);
      if (hasNLKeys(p)) return p;
    } catch (_) {}
    return null;
  };

  // 1. Direct JSON (clean or double-quoted wrapper)
  try {
    const parsed = JSON.parse(trimmed);
    if (hasNLKeys(parsed)) return parsed;
    const inner = parsed.result ?? parsed.response ?? null;
    if (typeof inner === 'string') {
      const r = parseFenced(inner);
      if (r) return r;
    }
    if (typeof inner === 'object' && hasNLKeys(inner)) return inner;
  } catch (_) {}

  // 2. Code fence at top level (no outer wrapper)
  const r2 = parseFenced(trimmed);
  if (r2) return r2;

  // 3. Python-repr single-quoted wrapper — extract via regex
  const respMatch = trimmed.match(/^[{(]'response':\s*'([\s\S]*?)',?\s*'stats':/);
  if (respMatch) {
    // unescape Python string escapes
    const unescaped = respMatch[1]
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, '\t')
      .replace(/\\r/g, '')
      .replace(/\\'/g, "'")
      .replace(/\\\\/g, '\\');
    const r3 = parseFenced(unescaped);
    if (r3) return r3;
    try { const p = JSON.parse(unescaped); if (hasNLKeys(p)) return p; } catch (_) {}
  }

  // 4. Plain-prose fallback (e.g. emails generated through the chat orchestrator
  //    rather than the structured email_newsletter quick-win endpoint). Parse:
  //      Subject: ...
  //      <blank line>
  //      body...
  //    Also handles "Subject Line:" and a leading **bold** subject. If no
  //    Subject line is present, the entire content goes into the body.
  if (trimmed) {
    const subjectMatch = trimmed.match(/^\s*(?:\*\*)?subject(?:\s*line)?\s*:\s*\*?\*?\s*([^\n]+)\n+/i);
    let subject_line = '';
    let body = trimmed;
    if (subjectMatch) {
      subject_line = subjectMatch[1].trim().replace(/\*+$/, '').trim();
      body = trimmed.slice(subjectMatch[0].length).trim();
    }
    const body_html = body
      .split(/\n{2,}/)
      .map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`)
      .join('\n');
    return {
      subject_line,
      body_plain: body,
      body_html,
    };
  }

  return null;
};

// ── Serialiser ────────────────────────────────────────────────────────────────
// Always saves as clean JSON — the view parser handles this format.
const serializeNewsletter = (data: NewsletterData): string =>
  JSON.stringify(data, null, 2);

// ── Body sync helpers ─────────────────────────────────────────────────────────
const plainToHtml = (plain: string): string =>
  (plain || '')
    .split(/\n{2,}/)
    .map(p => `<p>${p.replace(/\n/g, '<br>').trim()}</p>`)
    .filter(p => p !== '<p></p>')
    .join('\n');

const htmlToPlain = (html: string): string =>
  (html || '')
    // block-level tags become double newlines
    .replace(/<\/(p|div|h[1-6]|li|tr)>/gi, '\n\n')
    .replace(/<br\s*\/?>(\n)?/gi, '\n')
    // strip remaining tags
    .replace(/<[^>]+>/g, '')
    // decode common entities
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    // collapse 3+ newlines to 2
    .replace(/\n{3,}/g, '\n\n')
    .trim();

// ── Field styling helpers ─────────────────────────────────────────────────────
const labelStyle: React.CSSProperties = {
  display: 'block',
  fontWeight: 600,
  fontSize: '0.875rem',
  color: 'var(--text, #0f172a)',
  marginBottom: '0.35rem',
};
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.55rem 0.75rem',
  border: '1px solid var(--stroke, rgba(15,23,42,0.15))',
  borderRadius: '6px',
  background: 'var(--background, #f8fafc)',
  color: 'var(--text, #0f172a)',
  fontSize: '0.95rem',
  boxSizing: 'border-box',
};
const textareaStyle = (rows: number): React.CSSProperties => ({
  ...inputStyle,
  fontFamily: 'inherit',
  resize: 'vertical',
  minHeight: `${rows * 1.6}rem`,
});
const sectionStyle: React.CSSProperties = { marginBottom: '1.25rem' };
const charHintStyle: React.CSSProperties = { fontSize: '0.75rem', color: 'var(--text-secondary, #64748b)', marginTop: '0.2rem' };

// ── Component ─────────────────────────────────────────────────────────────────
const EditableNewsletterViewer: React.FC<EditableNewsletterViewerProps> = ({
  rawContent,
  onSave,
  onCancel,
  projectId,
}) => {
  const initial = parseNewsletterContent(rawContent) || {};
  const [fields, setFields] = useState<NewsletterData>(initial);
  const [altSubjects, setAltSubjects] = useState<string>(
    (initial.alternative_subjects || []).join('\n')
  );
  const [activeTab, setActiveTab] = useState<'visual' | 'html'>('visual');

  const set =
    (key: keyof NewsletterData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setFields(prev => ({ ...prev, [key]: e.target.value }));

  const setHtml = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFields(prev => ({ ...prev, body_html: e.target.value }));
  };

  const handleSave = () => {
    const body_html = fields.body_html || '';
    // body_plain is always auto-derived from HTML on save — it's the email
    // client fallback for HTML-disabled inboxes, not something the user edits.
    const body_plain = htmlToPlain(body_html);
    const data: NewsletterData = {
      ...fields,
      body_html,
      body_plain,
      alternative_subjects: altSubjects
        .split('\n')
        .map(s => s.trim())
        .filter(Boolean),
    };
    onSave(serializeNewsletter(data));
  };

  const tabBtn = (tab: 'visual' | 'html'): React.CSSProperties => ({
    padding: '0.4rem 1rem',
    border: '1px solid var(--stroke, rgba(15,23,42,0.15))',
    borderRadius: '4px',
    background: activeTab === tab ? 'var(--primary, #2177FF)' : 'var(--surface, #fff)',
    color: activeTab === tab ? '#fff' : 'var(--text, #0f172a)',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 500,
  });

  return (
    <div style={{ padding: '2rem', maxWidth: '860px', margin: '0 auto' }}>
      <h2 style={{ marginBottom: '1.75rem', color: 'var(--text, #0f172a)' }}>
        Edit Email / Newsletter
      </h2>

      {/* Subject line */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Subject Line</label>
        <input
          type="text"
          value={fields.subject_line || ''}
          onChange={set('subject_line')}
          style={inputStyle}
        />
        <span style={charHintStyle}>{(fields.subject_line || '').length} chars (aim for 40–60)</span>
      </div>

      {/* Preview text */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Preview Text</label>
        <input
          type="text"
          value={fields.preview_text || ''}
          onChange={set('preview_text')}
          style={inputStyle}
        />
        <span style={charHintStyle}>{(fields.preview_text || '').length} chars (aim for 85–100)</span>
      </div>

      {/* Body – tabbed Visual / HTML */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
          <span style={labelStyle}>Email Body</span>
          <button style={tabBtn('visual')} onClick={() => setActiveTab('visual')}>Visual</button>
          <button style={tabBtn('html')} onClick={() => setActiveTab('html')}>HTML</button>
        </div>
        {activeTab === 'visual' ? (
          <Suspense fallback={<div style={{ padding: '1rem', color: 'var(--text-secondary, #64748b)' }}>Loading editor…</div>}>
            <EDMRichEditor
              content={fields.body_html || ''}
              onChange={(html: string) => {
                setFields(prev => ({ ...prev, body_html: html }));
              }}
              projectId={projectId}
              placeholder="Email body…"
            />
          </Suspense>
        ) : (
          <textarea
            value={fields.body_html || ''}
            onChange={setHtml}
            style={{ ...textareaStyle(18), fontFamily: 'monospace', fontSize: '0.82rem' }}
          />
        )}
        <span style={charHintStyle}>
          Plain-text fallback (for HTML-disabled email clients) is generated automatically from your content on save.
        </span>
      </div>

      {/* Alternative subject lines */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Alternative Subject Lines <span style={{ fontWeight: 400 }}>(one per line)</span></label>
        <textarea
          value={altSubjects}
          onChange={e => setAltSubjects(e.target.value)}
          style={textareaStyle(4)}
        />
      </div>

      {/* Send time */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Recommended Send Time</label>
        <input
          type="text"
          value={fields.recommended_send_time || ''}
          onChange={set('recommended_send_time')}
          style={inputStyle}
          placeholder="e.g. Tuesday, 10:00 AM AEST"
        />
      </div>

      {/* Audience segment */}
      <div style={sectionStyle}>
        <label style={labelStyle}>Audience Segment</label>
        <textarea
          value={fields.audience_segment || ''}
          onChange={set('audience_segment')}
          style={textareaStyle(3)}
        />
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
        <button
          onClick={handleSave}
          style={{
            padding: '0.65rem 1.75rem',
            background: 'var(--primary, #2177FF)',
            color: '#fff',
            border: 'none',
            borderRadius: '9999px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '0.95rem',
          }}
        >
          Save
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: '0.65rem 1.75rem',
            background: 'var(--surface, #fff)',
            color: 'var(--text, #0f172a)',
            border: '1px solid var(--stroke, rgba(15,23,42,0.15))',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.95rem',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default EditableNewsletterViewer;
